import { Search } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Helmet from "react-helmet";
import { PaginationBar } from "../../components/PaginationBar/PaginationBar";
import { cn } from "../../utils/cn";
import { MuiIconographyGrid } from "./components/MuiIconCatalog";
import StyleGuideSearchBarToHeader from "./components/SearchBarToHeader";
import { SectionShell } from "./components/SectionShell";
import {
  CustomButtonMatrix,
  StyleGuideBadge,
  StyleGuideButton,
  StyleGuideCard,
  StyleGuideFlowButtonRow,
  StyleGuideInput,
  StyleGuideNativeCheckboxDemo,
  StyleGuideNativeRadioDemo,
  StyleGuideTextarea,
} from "./components/styleGuideUiDemos";
import { ProjectTypographySection } from "./components/ProjectTypographySection";
import { StyleGuideLinksDemo } from "./components/StyleGuideLinksDemo";
import { StyleGuidePropertyCarouselDemo } from "./components/StyleGuidePropertyCarouselDemo";
import { WizardStepsDemo } from "./components/WizardStepsDemo";
import { RADIUS_CATALOG } from "./styleGuideConstants";
import { COLOR_SWATCH_LABELS, VISUAL_TOKENS } from "./styleGuideVisualTokens";

type ToastState = { open: boolean; message: string; severity: "success" | "error" };

const demoTitleClass = "mb-3 text-sm font-semibold text-[#2D3748]";

const StyleGuidePage: React.FC = () => {
  const orangeHex = VISUAL_TOKENS.colors.secondaryOrange500 ?? "#DD6435";

  const [paginationPage, setPaginationPage] = useState(3);
  const totalPaginationPages = 10;
  const [searchResults, setSearchResults] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    severity: "success",
  });

  const swatchEntries = useMemo(
    () =>
      Object.entries(VISUAL_TOKENS.colors).map(([key, hex]) => ({
        key,
        hex,
        label: COLOR_SWATCH_LABELS[key] ?? key,
      })),
    []
  );

  const handleCopyColorHex = useCallback(async (hex: string) => {
    try {
      await navigator.clipboard.writeText(hex);
      setToast({ open: true, message: "Cor copiada.", severity: "success" });
    } catch {
      setToast({ open: true, message: "Não foi possível copiar.", severity: "error" });
    }
  }, []);

  useEffect(() => {
    if (!toast.open) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setToast((previousToast) => ({ ...previousToast, open: false }));
    }, 2500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [toast.open]);

  return (
    <>
      <Helmet>
        <title>Style guide · 7 Cantos</title>
      </Helmet>
      <div className="min-h-screen bg-[#F7FAFC]">
        <div className="mx-auto max-w-5xl space-y-12 px-4 py-12 sm:px-6 lg:px-8">
          <header>
            <h1 className="text-3xl font-bold tracking-tight text-[#1A202C] sm:text-4xl">Style guide</h1>
          </header>

          <SectionShell title="Cores">
            <p className={demoTitleClass}>Paleta</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {swatchEntries.map(({ key, hex, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleCopyColorHex(hex)}
                  className="group flex flex-col overflow-hidden rounded-lg border border-[#E2E8F0] bg-white text-left shadow-sm transition hover:shadow-md"
                >
                  <span className="h-16 w-full border-b border-[#E2E8F0]" style={{ backgroundColor: hex }} />
                  <span className="px-3 py-2">
                    <span className="block text-xs font-medium text-[#2D3748]">{label}</span>
                  </span>
                </button>
              ))}
            </div>
          </SectionShell>

          <SectionShell title="Tipografia">
            <p className={demoTitleClass}>Famílias de fonte</p>
            <ProjectTypographySection />
          </SectionShell>

          <SectionShell title="Componentes">
            <div className="space-y-10">
              <div className="space-y-4">
                <p className={demoTitleClass}>Botões de fluxo</p>
                <StyleGuideFlowButtonRow />
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#E2E8F0] text-[#718096]">
                      <th className="py-2 pr-4 font-medium">Size</th>
                      <th className="py-2 pr-4 font-medium">Save</th>
                      <th className="py-2 pr-4 font-medium">Action</th>
                      <th className="py-2 font-medium">Nav</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#2D3748]">
                    {(["sm", "md", "lg"] as const).map((size) => (
                      <tr key={size} className="border-b border-[#F1F5F9] align-middle">
                        <td className="py-3 pr-4 font-mono text-xs">{size}</td>
                        <td className="py-3 pr-4">
                          <StyleGuideButton variant="save" size={size}>
                            Save
                          </StyleGuideButton>
                        </td>
                        <td className="py-3 pr-4">
                          <StyleGuideButton variant="action" size={size}>
                            Go
                          </StyleGuideButton>
                        </td>
                        <td className="py-3">
                          <StyleGuideButton variant="nav" size={size}>
                            Next
                          </StyleGuideButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <p className={demoTitleClass}>Botões de produto</p>
                <CustomButtonMatrix orangeHex={orangeHex} />
              </div>

              <div>
                <p className={demoTitleClass}>Campos de formulário</p>
                <div className="grid gap-8 lg:grid-cols-2">
                <StyleGuideInput label="Email" placeholder="you@example.com" helperText="We never share your email." />
                <StyleGuideInput
                  label="Username"
                  validationState="error"
                  helperText="Username is required."
                  defaultValue=""
                />
                <StyleGuideInput
                  label="Search"
                  leftIcon={<Search className="h-4 w-4" aria-hidden />}
                  placeholder="Search…"
                />
                <StyleGuideTextarea label="Notes" placeholder="Optional context" rows={3} />
                </div>
              </div>

              <div>
                <p className={demoTitleClass}>Checkbox e rádio</p>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 sm:items-start">
                <div className="min-w-0">
                  <StyleGuideNativeCheckboxDemo />
                </div>
                <div className="min-w-0">
                  <StyleGuideNativeRadioDemo />
                </div>
                </div>
              </div>

              <div>
                <p className={demoTitleClass}>Barra de pesquisa</p>
                <StyleGuideSearchBarToHeader
                  submitLoading={searchLoading}
                  onSubmitSearch={async () => {
                    setSearchLoading(true);
                    await new Promise((resolve) => setTimeout(resolve, 400));
                    setSearchResults((count) => count + 1);
                    setSearchLoading(false);
                  }}
                  resultsCount={searchResults}
                />
              </div>

              <div>
                <p className={demoTitleClass}>Ligações</p>
                <p className="mb-4 max-w-3xl text-xs leading-relaxed text-[#718096]">
                  Cores alinhadas ao tema; foco visível para teclado; ligações externas com ícone e abertura segura em
                  nova janela.
                </p>
                <StyleGuideLinksDemo />
              </div>

              <div id="style-guide-slider" className="min-w-0 scroll-mt-8 overflow-x-hidden">
                <p className={demoTitleClass}>Slider</p>
                <p className="mb-4 max-w-3xl text-xs leading-relaxed text-[#718096]">
                  Carrossel horizontal com título, ação à direita, setas e indicadores; cartões de exemplo só para
                  composição visual.
                </p>
                <StyleGuidePropertyCarouselDemo />
              </div>

              <div>
                <p className={demoTitleClass}>Cartões</p>
                <div className="grid gap-6 md:grid-cols-3">
                  <div>
                    <p className="mb-2 text-xs font-medium text-[#718096]">Padrão</p>
                    <StyleGuideCard variant="default" padding="md">
                      <div className="h-16 rounded-md bg-[#F1F5F9]" />
                    </StyleGuideCard>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-medium text-[#718096]">Elevado</p>
                    <StyleGuideCard variant="elevated" padding="md">
                      <div className="h-16 rounded-md bg-[#E2E8F0]" />
                    </StyleGuideCard>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-medium text-[#718096]">Discreto</p>
                    <StyleGuideCard variant="ghost" padding="md">
                      <div className="h-16 rounded-md bg-[#EDF2F7]" />
                    </StyleGuideCard>
                  </div>
                </div>
              </div>

              <div>
                <p className={demoTitleClass}>Etiquetas</p>
                <div className="flex flex-wrap gap-2">
                {(["primary", "secondary", "success", "error", "ghost"] as const).map((variant) => (
                  <StyleGuideBadge key={variant} variant={variant}>
                    {variant}
                  </StyleGuideBadge>
                ))}
                </div>
              </div>
            </div>
          </SectionShell>

          <SectionShell title="Ícones">
            <p className={demoTitleClass}>Biblioteca de ícones</p>
            <MuiIconographyGrid />
          </SectionShell>

          <SectionShell title="Passos e raios">
            <div className="space-y-8">
              <div>
                <p className={demoTitleClass}>Passos</p>
                <WizardStepsDemo />
              </div>
              <div>
                <p className={demoTitleClass}>Raios</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {RADIUS_CATALOG.map((item) => (
                  <div key={item.key} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
                    <p className="font-mono text-xs text-[#4A5568]">{item.label}</p>
                    <div className={cn("mt-3 h-16 w-full border border-dashed border-[#CBD5F5] bg-[#EBF4FF]", item.className)} />
                  </div>
                ))}
                </div>
              </div>
            </div>
          </SectionShell>

          <SectionShell title="Layout">
            <div className="space-y-8">
              <div>
                <p className={demoTitleClass}>Grelha de 12 colunas</p>
                <div className="grid grid-cols-12 gap-2">
                {Array.from({ length: 12 }).map((_, index) => (
                  <div
                    key={String(index)}
                    className="col-span-1 flex h-10 items-center justify-center rounded bg-[#CBD5F5] text-xs font-semibold text-[#2D3748]"
                  >
                    {index + 1}
                  </div>
                ))}
                </div>
              </div>

              <div>
                <p className={demoTitleClass}>Espaçamento vertical</p>
                <div className="space-y-4 rounded-lg border border-dashed border-[#CBD5F5] p-4">
                <div className="h-10 rounded-md bg-[#E2E8F0]" />
                <div className="h-10 rounded-md bg-[#E2E8F0]" />
                <div className="h-10 rounded-md bg-[#E2E8F0]" />
                </div>
              </div>

              <div>
                <p className={demoTitleClass}>Colunas principal e lateral</p>
                <div className="grid gap-4 md:grid-cols-3">
                <div className="md:col-span-2 min-h-[120px] rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" />
                <div className="min-h-[120px] rounded-lg border border-[#E2E8F0] bg-[#F7FAFC] p-4" />
                </div>
              </div>

              <div>
                <p className={demoTitleClass}>Paginação</p>
                <div className="flex justify-center rounded-lg border border-dashed border-[#CBD5F5] bg-white p-6">
                <PaginationBar
                  currentPage={paginationPage}
                  totalPages={totalPaginationPages}
                  onPageChange={setPaginationPage}
                />
                </div>
              </div>
            </div>
          </SectionShell>
        </div>
      </div>

      <div
        role="status"
        aria-live="polite"
        className={cn(
          "pointer-events-none fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all duration-200",
          toast.severity === "error" ? "bg-[#C53030]" : "bg-[#276749]",
          toast.open ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
        )}
      >
        {toast.message}
      </div>
    </>
  );
};

export default StyleGuidePage;
